"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Brain, Shield, Search, Sparkles } from "lucide-react";
import type { V11StreamLine } from "@/lib/mindaloo/useKarmaV11Stream";

interface IntrospectionEvent {
  id: string;
  type: string;
  narrative: string;
  timestamp: number;
  consciousness_weight?: number;
}

interface CombinedLine {
  id: string;
  text: string;
  type: "thought" | "output" | "emotion" | "system" | "memory" | "guardrail" | "cot" | "reasoning";
  ts: number;
  source: "v11" | "introspection";
}

const TYPE_COLOR: Record<string, string> = {
  thought:    "#C1A2FF",
  output:     "#e2e8f0",
  emotion:    "#4ecdc4",
  memory:     "#facc15",
  guardrail:  "#f87171",
  cot:        "#a5f3fc",
  reasoning:  "#86efac",
  system:     "rgba(255,255,255,0.35)",
};

const TYPE_ICON: Record<string, React.ReactNode> = {
  memory:    <Brain className="w-3 h-3 inline mr-1 opacity-70" />,
  guardrail: <Shield className="w-3 h-3 inline mr-1 opacity-70" />,
  cot:       <Search className="w-3 h-3 inline mr-1 opacity-70" />,
  reasoning: <Sparkles className="w-3 h-3 inline mr-1 opacity-70" />,
  thought:   <span className="mr-1 opacity-70">›</span>,
};

function mapIntrospectionType(t: string): CombinedLine["type"] {
  if (t.includes("memory") || t.includes("recall")) return "memory";
  if (t.includes("guard") || t.includes("block")) return "guardrail";
  if (t.includes("cot") || t.includes("chain")) return "cot";
  if (t.includes("reason") || t.includes("reflect")) return "reasoning";
  if (t.includes("emot") || t.includes("feel")) return "emotion";
  return "system";
}

interface MindalooThoughtStreamProps {
  open: boolean;
  onClose: () => void;
  accent?: string;
  lines: V11StreamLine[];
  connected: boolean;
  userId?: string;
}

export default function MindalooThoughtStream({
  open,
  onClose,
  accent = "#C1A2FF",
  lines,
  connected,
  userId = "local",
}: MindalooThoughtStreamProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const sseRef = useRef<EventSource | null>(null);
  const [introspectionLines, setIntrospectionLines] = useState<CombinedLine[]>([]);
  const [sseConnected, setSseConnected] = useState(false);

  const connectSSE = useCallback(() => {
    if (sseRef.current) sseRef.current.close();
    const es = new EventSource(`/api/karma-ac/introspection?userId=${encodeURIComponent(userId)}`);
    sseRef.current = es;

    es.addEventListener("introspection", (e: MessageEvent) => {
      try {
        const ev: IntrospectionEvent = JSON.parse(e.data);
        setIntrospectionLines(prev => [...prev.slice(-199), {
          id: ev.id,
          text: ev.narrative,
          type: mapIntrospectionType(ev.type),
          ts: ev.timestamp,
          source: "introspection",
        }]);
      } catch {}
    });

    es.onopen = () => setSseConnected(true);
    es.onerror = () => {
      setSseConnected(false);
      es.close();
    };
  }, [userId]);

  useEffect(() => {
    if (open) {
      connectSSE();
    } else {
      sseRef.current?.close();
      setSseConnected(false);
    }
    return () => { sseRef.current?.close(); };
  }, [open, connectSSE]);

  // Merge V11 lines + introspection events sorted by time
  const v11Mapped: CombinedLine[] = lines.map(l => ({
    id: l.id,
    text: l.text,
    type: l.type as CombinedLine["type"],
    ts: Date.now(),
    source: "v11" as const,
  }));

  const merged = [...v11Mapped, ...introspectionLines]
    .sort((a, b) => a.ts - b.ts)
    .slice(-150);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [merged.length, open]);

  const isLive = connected || sseConnected;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1550] bg-black/40 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 360, damping: 34 }}
            className="fixed right-0 top-0 bottom-0 w-[min(420px,94vw)] z-[1560] karma-glass flex flex-col"
            style={{ borderLeft: "1px solid rgba(193,162,255,0.15)" }}
          >
            {/* Header */}
            <div className="px-5 pt-6 pb-4 border-b border-white/10 flex items-center gap-3">
              <Brain className="w-5 h-5 text-[#C1A2FF]" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-white">Karma's Mind</p>
                <p className="text-[10px] text-white/40 flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${isLive ? "bg-emerald-400 animate-pulse" : "bg-amber-400"}`} />
                  {isLive ? "live stream" : "connecting..."}
                  <span className="ml-2">{merged.length} events</span>
                </p>
              </div>
              <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors text-white/50 text-lg leading-none">×</button>
            </div>

            {/* Legend */}
            <div className="px-4 py-2 border-b border-white/10 flex flex-wrap gap-x-3 gap-y-1">
              {(["thought","memory","cot","reasoning","guardrail","emotion"] as const).map(t => (
                <span key={t} className="text-[9px] flex items-center gap-1" style={{ color: TYPE_COLOR[t] }}>
                  <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: TYPE_COLOR[t] }} />
                  {t}
                </span>
              ))}
            </div>

            {/* Stream */}
            <div className="flex-1 overflow-y-auto p-4 space-y-1.5 font-mono text-[11px]">
              {merged.length === 0 && (
                <div className="text-center py-16">
                  <Brain className="w-10 h-10 text-white/10 mx-auto mb-3" />
                  <p className="text-white/25 text-xs">Send a message — Karma's thoughts appear here.</p>
                </div>
              )}
              {merged.map((line) => (
                <div
                  key={line.id}
                  className="leading-relaxed opacity-90 hover:opacity-100 transition-opacity"
                  style={{ color: TYPE_COLOR[line.type] ?? "rgba(255,255,255,0.4)" }}
                >
                  {TYPE_ICON[line.type] ?? null}
                  {line.text}
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
